import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, formatApiError } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { Sparkles, Locate } from 'lucide-react';
import { toast } from 'sonner';
import MapView from '@/components/MapView';

const CROPS = ['Vite','Olivo','Grano','Mais','Ortaggi','Frutteto','Foraggio','Nocciolo'];
const FEATS = [
  ['water','Acqua'], ['irrigation','Irrigazione'], ['electricity','Elettricità'],
  ['road_access','Accesso stradale'], ['buildings','Fabbricati'], ['fenced','Recinzioni'],
];

export default function Preferences() {
  const nav = useNavigate();
  const [meta, setMeta] = useState({ fruit_types: [], quality_grades: [] });
  const [p, setP] = useState({
    listing_type: '', region: '',
    area_min: '', area_max: '',
    budget_min: '', budget_max: '',
    radius_km: 50,
    lat: 42.5, lng: 12.5,
    crops: [], required_features: [],
    // Batch prefs
    batch_fruit_types: [], batch_qualities: [],
    batch_price_min: '', batch_price_max: '',
    batch_qty_min: '', batch_qty_max: '',
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/batches/meta').then(r => setMeta(r.data));
    api.get('/auth/me').then(r => {
      const prefs = r.data?.preferences || {};
      setP(cur => ({
        ...cur,
        ...prefs,
        crops: prefs.crops || [],
        required_features: prefs.required_features || [],
        batch_fruit_types: prefs.batch_fruit_types || [],
        batch_qualities: prefs.batch_qualities || [],
        radius_km: prefs.radius_km ?? 50,
        lat: prefs.lat ?? 42.5, lng: prefs.lng ?? 12.5,
      }));
    });
  }, []);

  const toggleCrop = (c) => setP({ ...p, crops: p.crops.includes(c) ? p.crops.filter(x=>x!==c) : [...p.crops, c] });
  const toggleFeat = (k) => setP({ ...p, required_features: p.required_features.includes(k) ? p.required_features.filter(x=>x!==k) : [...p.required_features, k] });
  const toggleFruit = (f) => setP({ ...p, batch_fruit_types: p.batch_fruit_types.includes(f) ? p.batch_fruit_types.filter(x=>x!==f) : [...p.batch_fruit_types, f] });
  const toggleQuality = (q) => setP({ ...p, batch_qualities: p.batch_qualities.includes(q) ? p.batch_qualities.filter(x=>x!==q) : [...p.batch_qualities, q] });

  const locate = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(pos => setP({ ...p, lat: pos.coords.latitude, lng: pos.coords.longitude }));
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        listing_type: p.listing_type || null,
        region: p.region || null,
        area_min: p.area_min !== '' ? Number(p.area_min) : null,
        area_max: p.area_max !== '' ? Number(p.area_max) : null,
        budget_min: p.budget_min !== '' ? Number(p.budget_min) : null,
        budget_max: p.budget_max !== '' ? Number(p.budget_max) : null,
        radius_km: Number(p.radius_km),
        lat: Number(p.lat), lng: Number(p.lng),
        crops: p.crops, required_features: p.required_features,
        batch_fruit_types: p.batch_fruit_types,
        batch_qualities: p.batch_qualities,
        batch_price_min: p.batch_price_min !== '' ? Number(p.batch_price_min) : null,
        batch_price_max: p.batch_price_max !== '' ? Number(p.batch_price_max) : null,
        batch_qty_min: p.batch_qty_min !== '' ? Number(p.batch_qty_min) : null,
        batch_qty_max: p.batch_qty_max !== '' ? Number(p.batch_qty_max) : null,
      };
      await api.put('/users/me/preferences', payload);
      toast.success('Preferenze salvate — ora vedi terreni e partite compatibili');
      nav('/suggeriti');
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-12">
      <div className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta mb-3">Preferenze di ricerca</div>
      <h1 className="font-display text-4xl md:text-5xl tracking-tighter">Cosa stai cercando?</h1>
      <p className="mt-4 text-muted-foreground max-w-xl">Le tue preferenze vengono usate per calcolare un punteggio di compatibilità <b>trasparente</b> per ogni terreno. Ogni fattore viene motivato con un +/−.</p>

      <div className="mt-10 rounded-2xl border border-border p-8 bg-card space-y-8">
        <section>
          <Label className="text-xs uppercase tracking-widest">Operazione</Label>
          <div className="mt-3 grid grid-cols-3 gap-2">
            {[['','Qualsiasi'],['sale','Vendita'],['rent','Affitto']].map(([v,l])=>(
              <button key={v} type="button" onClick={()=>setP({...p, listing_type:v})}
                      className={`rounded-xl border p-3 text-sm text-left ${p.listing_type===v?'border-foreground bg-secondary':'border-border'}`}>{l}</button>
            ))}
          </div>
        </section>

        <section className="grid md:grid-cols-2 gap-4">
          <div><Label>Regione (facoltativa)</Label><Input value={p.region} onChange={e=>setP({...p, region:e.target.value})} placeholder="Es. Toscana"/></div>
          <div>
            <Label>Raggio: {p.radius_km} km</Label>
            <Slider value={[p.radius_km]} min={5} max={500} step={5} onValueChange={([v])=>setP({...p, radius_km:v})} className="mt-4"/>
          </div>
        </section>

        <section>
          <div className="flex items-center justify-between">
            <Label className="text-xs uppercase tracking-widest">Zona centrale</Label>
            <Button variant="outline" size="sm" onClick={locate} className="rounded-full"><Locate size={12} className="mr-1"/> Usa la mia posizione</Button>
          </div>
          <div className="mt-3 text-xs text-muted-foreground">Clicca sulla mappa per selezionare il punto di riferimento.</div>
          <div className="mt-3">
            <MapView height="320px" center={[p.lat, p.lng]} zoom={p.region?7:6}
                     pickedPoint={{lat:p.lat, lng:p.lng}}
                     onPick={(ll)=>setP({...p, lat: ll.lat, lng: ll.lng})}/>
          </div>
        </section>

        <section className="grid md:grid-cols-2 gap-4">
          <div><Label>Superficie min (ha)</Label><Input type="number" value={p.area_min} onChange={e=>setP({...p, area_min:e.target.value})}/></div>
          <div><Label>Superficie max (ha)</Label><Input type="number" value={p.area_max} onChange={e=>setP({...p, area_max:e.target.value})}/></div>
          <div><Label>Budget min (€)</Label><Input type="number" value={p.budget_min} onChange={e=>setP({...p, budget_min:e.target.value})}/></div>
          <div><Label>Budget max (€)</Label><Input type="number" value={p.budget_max} onChange={e=>setP({...p, budget_max:e.target.value})}/></div>
        </section>

        <section>
          <Label className="text-xs uppercase tracking-widest">Colture di interesse</Label>
          <div className="mt-3 flex flex-wrap gap-2">
            {CROPS.map(c => (
              <button key={c} type="button" onClick={()=>toggleCrop(c)}
                className={`text-xs px-3 py-1.5 rounded-full border ${p.crops.includes(c)?'bg-foreground text-background border-foreground':'border-border'}`}>{c}</button>
            ))}
          </div>
        </section>

        <section>
          <Label className="text-xs uppercase tracking-widest">Caratteristiche richieste</Label>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
            {FEATS.map(([k,l])=>(
              <label key={k} className={`rounded-xl border p-3 text-sm flex items-center gap-2 cursor-pointer ${p.required_features.includes(k)?'border-foreground bg-secondary':'border-border'}`}>
                <Checkbox checked={p.required_features.includes(k)} onCheckedChange={()=>toggleFeat(k)}/> {l}
              </label>
            ))}
          </div>
        </section>

        <div className="pt-6 border-t border-border">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta mb-2">Marketplace partite</div>
          <h2 className="font-display text-2xl">Cosa vuoi acquistare come raccolto?</h2>
          <p className="mt-2 text-sm text-muted-foreground max-w-xl">Queste preferenze alimentano <b>/partite/suggerite</b> con un punteggio trasparente per ogni partita disponibile.</p>
        </div>

        <section>
          <Label className="text-xs uppercase tracking-widest">Tipo prodotto di interesse</Label>
          <div className="mt-3 flex flex-wrap gap-2">
            {meta.fruit_types.map(t => (
              <button key={t} type="button" onClick={()=>toggleFruit(t)}
                data-testid={`pref-fruit-${t}`}
                className={`text-xs px-3 py-1.5 rounded-full border ${p.batch_fruit_types.includes(t)?'bg-foreground text-background border-foreground':'border-border hover:bg-secondary'}`}>{t}</button>
            ))}
          </div>
        </section>

        <section>
          <Label className="text-xs uppercase tracking-widest">Qualità accettate</Label>
          <div className="mt-3 flex flex-wrap gap-2">
            {meta.quality_grades.map(q => {
              const labels = { standard:'Standard', biologico:'Biologico', igp:'IGP', dop:'DOP', in_conversione:'In conversione bio' };
              return (
                <button key={q} type="button" onClick={()=>toggleQuality(q)}
                  data-testid={`pref-quality-${q}`}
                  className={`text-xs px-3 py-1.5 rounded-full border ${p.batch_qualities.includes(q)?'bg-foreground text-background border-foreground':'border-border hover:bg-secondary'}`}>{labels[q] || q}</button>
              );
            })}
          </div>
        </section>

        <section className="grid md:grid-cols-2 gap-4">
          <div><Label>Budget min per quintale (€)</Label><Input type="number" value={p.batch_price_min} onChange={e=>setP({...p, batch_price_min:e.target.value})}/></div>
          <div><Label>Budget max per quintale (€)</Label><Input type="number" value={p.batch_price_max} onChange={e=>setP({...p, batch_price_max:e.target.value})}/></div>
          <div><Label>Quantità min (q)</Label><Input type="number" value={p.batch_qty_min} onChange={e=>setP({...p, batch_qty_min:e.target.value})}/></div>
          <div><Label>Quantità max (q)</Label><Input type="number" value={p.batch_qty_max} onChange={e=>setP({...p, batch_qty_max:e.target.value})}/></div>
        </section>

        <Button onClick={save} disabled={busy} className="rounded-full h-11">
          <Sparkles size={14} className="mr-2"/> {busy?'Salvataggio…':'Salva e mostra terreni compatibili'}
        </Button>
      </div>
    </div>
  );
}
